import { isNil } from 'lodash'
import { HeatingAirHeatPumpParameters } from '../../generated/graphql'
import { EnergyType } from '../EnergyFlow'
import { mapValueBasedOnMaxPower } from './util/value-mapper'
import {
  createHourlyFromEnvironment,
  createHourlyProvided,
  createHourlyRequirement,
  HourlyEnergyFlow,
} from '../HourlyEnergyFlow'
import { OutdoorTemperatureMissingError } from '../calculator-error'
import {
  SingleTypeHourlyVariables,
  SingleTypeProvider,
} from './common/SingleTypeProvider'

/**
 * Converts electricity to heating with a limited power.
 */
export class HeatingAirHeatPumpProvider extends SingleTypeProvider<HeatingAirHeatPumpParameters> {
  public readonly providedEnergyTypes = [EnergyType.Heating]

  protected provideForHour({
    remainingDemand,
    outdoorTemperature,
  }: SingleTypeHourlyVariables): HourlyEnergyFlow[] {
    const { cop } = this.parameters

    if (isNil(outdoorTemperature)) {
      throw new OutdoorTemperatureMissingError()
    }

    const energy =
      outdoorTemperature < this.parameters.minOutdoorTemperature
        ? 0
        : mapValueBasedOnMaxPower(
            remainingDemand.value,
            this.parameters.maxPower,
          )

    const provided = createHourlyProvided(
      remainingDemand.demandId,
      remainingDemand.energyType,
      energy,
    )

    const requiredConsumption = createHourlyRequirement(
      provided.demandId,
      EnergyType.Electricity,
      provided.value / cop,
    )

    const fromEnvironment = createHourlyFromEnvironment(
      provided.demandId,
      provided.energyType,
      provided.value - provided.value / cop,
    )

    return [provided, requiredConsumption, fromEnvironment]
  }
}
